'use client';

import { useState, useEffect } from 'react';
import { useChatHar } from '../../har/ChatHarProvider';

type UseToolCallHarAvailabilityParams = {
  toolCallId: string;
  refreshKey?: string;
  enabled?: boolean;
};

export function useToolCallHarAvailability({
  toolCallId,
  refreshKey,
  enabled = true,
}: UseToolCallHarAvailabilityParams) {
  const { fetchHarData, hasHarPresence } = useChatHar();
  const [harCount, setHarCount] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(enabled);
  const harIsPresent = hasHarPresence?.(toolCallId) ?? false;

  useEffect(() => {
    if (!enabled || !toolCallId) {
      setHarCount(null);
      setIsLoading(false);

      return;
    }

    let isMounted = true;

    const loadHarCount = async () => {
      setIsLoading(true);

      try {
        const hars = await fetchHarData(toolCallId);

        if (isMounted) {
          setHarCount(hars.length);
          setIsLoading(false);
        }
      } catch (error: unknown) {
        console.warn('Failed to fetch HAR count; continuing without HAR', {
          error: error instanceof Error ? error : new Error(String(error)),
          toolCallId,
        });

        if (isMounted) {
          setHarCount(0);
          setIsLoading(false);
        }
      }
    };

    void loadHarCount();

    return () => {
      isMounted = false;
    };
  }, [enabled, toolCallId, fetchHarData, refreshKey, harIsPresent]);

  return {
    harCount,
    isLoading,
    harIsPresent,
  };
}
